/**
 * Google sign-in return. The backend's callback (after checking the OAuth `state`) redirects
 * to `/?google_code=CODE`: a one-time code, valid 60 seconds, that this component exchanges for
 * the session token (SEC3: the token itself never appears in a URL, the history or a log). The
 * code is taken from the URL at once. It is exchanged only if this tab started a Google sign-in
 * (`markOAuthPending`), so a link carrying someone else's code cannot sign the visitor into that
 * account (login CSRF). Then the app follows the validated `?redirect=` stored at the start, else
 * Početna; a refused code lands on Login with the Google error. Only on "/": the emailed
 * /reset-password and /confirm-email links carry their own ?token=.
 *
 * The old `/?token=` return no longer signs in: it is only removed from the URL.
 *
 * Any other load of the app in this tab (the Google error redirect to /login?error=, Back from
 * Google, a reload) or a back/forward-cache restore drops the marker, so an abandoned sign-in
 * does not leave the tab accepting a later `/?google_code=` link.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { authApi } from '@/services/api';
import { safeRedirect } from '@/utils/redirect';
import { clearOAuthPending, takeOAuthPending } from '@/utils/oauthPending';
import { ROUTES } from '@/config/routes';

const CODE_PARAM = 'google_code';
const GOOGLE_FAILED = `${ROUTES.login}?error=google_auth_failed`;

export function OAuthTokenHandler() {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const checkAuth = useAuthStore((state) => state.checkAuth);
  // StrictMode runs effects twice; the marker and the code are single use, so handle each once.
  const handled = useRef<string | null>(null);
  // The location the app was loaded at, read once: only the Google return keeps the marker.
  const loadedWithCode = useRef(pathname === ROUTES.home && searchParams.has(CODE_PARAM));

  useEffect(() => {
    if (!loadedWithCode.current) clearOAuthPending();
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) clearOAuthPending();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    if (pathname !== ROUTES.home) return;
    const code = searchParams.get(CODE_PARAM);
    if (!code) {
      // A pre-SEC3 return link: never a sign-in now, just not left in the URL.
      if (searchParams.has('token')) navigate(ROUTES.home, { replace: true });
      return;
    }
    if (handled.current === code) return;
    handled.current = code;

    const pending = takeOAuthPending();
    navigate(ROUTES.home, { replace: true });
    if (!pending) return;

    void authApi
      .exchangeGoogleCode(code)
      .then(async () => {
        await checkAuth();
        navigate(safeRedirect(pending.redirect) ?? ROUTES.home, { replace: true });
      })
      .catch(() => navigate(GOOGLE_FAILED, { replace: true }));
  }, [pathname, searchParams, navigate, checkAuth]);

  return null;
}
